import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Avatar from "@/assets/images/brain.png";
import IcCalendar from "@/assets/icons/ic-calendar-02.svg";
import IcPencil from "@/assets/icons/ic-pencil.svg";
import IcUserNew from "@/assets/icons/ic-user-new.svg";
import IcPhone from "@/assets/icons/ic-phone.svg";
import IcMail01 from "@/assets/icons/ic-mail-01.svg";
import IcUserCircle from "@/assets/icons/ic-user-circle.svg";
import IcArrowNarrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import IcCheck from "@/assets/icons/ic-check.svg";
// import Avatar from "@/assets/images/avatar.png";
import { InquiryDetail, InquiryReply } from "@/types/inquiry";
import {
  completeInquiry,
  getInquiryDetail,
  sendInquiryReply,
  markInquiryRead,
} from "@/services/manage-inquiry";
import { formatDate, formatPhoneNumber, STATUS_CONFIG_INQUIRY } from "@/lib/utils";
import { usePermissions } from "@/lib/permissions";

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  completed: { label: "답변완료", bg: "bg-[#43946C]", text: "text-[#000000]" },
  in_progress: {
    label: "처리 중",
    bg: "bg-[#FFAA72]",
    text: "text-[#000000]",
  },
};

export default function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canReply = can.create("inquiry_management");
  const canComplete = can.update("inquiry_management");
  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [replyText]);

  useEffect(() => {
    if (!id) return;
    const fetchDetail = async () => {
      try {
        const data = await getInquiryDetail(id);
        setInquiry(data);
        if (!data.read_at) {
          await markInquiryRead(id);
        }
      } catch {
        setInquiry(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const handleSendReply = async () => {
    if (!id || !replyText.trim() || isSending) return;
    setIsSending(true);
    try {
      const newReply = await sendInquiryReply(id, replyText.trim());
      setInquiry((prev) =>
        prev ? { ...prev, messages: [...(prev.messages ?? []), newReply] } : prev,
      );
      setReplyText("");
    } finally {
      setIsSending(false);
    }
  };

  const handleComplete = async () => {
    if (!id || isCompleting) return;
    setIsCompleting(true);
    try {
      await completeInquiry(id);
      setInquiry((prev) => (prev ? { ...prev, status: "completed" } : prev));
    } finally {
      setIsCompleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-primary" />
      </div>
    );
  }

  if (!inquiry) {
    return null;
  }

  const statusConfig = STATUS_CONFIG_INQUIRY[inquiry.status] || STATUS_CONFIG.in_progress;

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-primary" />
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center gap-6 rounded-2xl bg-white w-full w-[896px] mx-auto "
        style={{ display: isLoading ? "none" : "flex" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => navigate("/inquiries")}
              className="w-12 h-12  flex items-center justify-center "
            >
              <img src={IcArrowNarrowLeft} alt="Back" className="w-6 h-6" />
            </button>
            <h2 className="text-[20px] font-bold leading-7 tracking-[-0.02em] text-[#202020]">
              뒤로 가기
            </h2>
          </div>

          {/* 완료 처리 */}
          {canComplete && inquiry.status !== "completed" && (
            <button
              type="button"
              disabled={isCompleting}
              onClick={handleComplete}
              className="h-12 px-5 rounded  bg-primary text-black font-bold text-base flex items-center gap-2  transition-colors disabled:opacity-40"
            >
              {/* <img src={IcCheck} alt="" className="w-6 h-6" /> */}
              {isCompleting ? "처리 중..." : "완료 처리"}
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-black/[0.09]" />

        {/* Content */}
        <div className="flex flex-col gap-6 w-full">
          {/* Card 1: Title + Meta */}
          <div className="flex flex-col gap-6 p-6 rounded border border-black/[0.09]">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <h1 className="flex-1 text-[32px] font-normal leading-10 tracking-[-0.03em] text-[#202020]  whitespace-pre-line break-all ">
                  {inquiry.title}
                </h1>
                <span
                  className={`inline-flex items-center leading-6 justify-center gap-1  py-[4px] rounded text-lg tracking-[-0.01em] ${statusConfig.bg} ${statusConfig.text} ${statusConfig.padding}`}
                >
                  {statusConfig.label}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <img src={IcCalendar} alt="" className="w-5 h-5" />
                    <span className="text-sm text-[#B0B0B0]">접수일시</span>
                  </div>
                  <div className="text-base text-[#202020]">
                    {formatDate(inquiry.created_at, "YYYY-MM-DD HH:mm")}
                  </div>
                </div>
                {inquiry.last_reply_at && (
                  <div className="flex items-center gap-2">
                    <img src={IcCalendar} alt="" className="w-5 h-5" />
                    <span className="text-sm text-[#B0B0B0]">답변일시</span>
                    <span className="text-base text-[#202020] ml-3">
                      {formatDate(inquiry.last_reply_at, "YYYY-MM-DD HH:mm")}
                    </span>
                  </div>
                )}
                {inquiry.admin_name && (
                  <div className="flex items-center gap-2">
                    <img src={IcPencil} alt="" className="w-5 h-5" />
                    <span className="text-sm text-[#B0B0B0]">담당자</span>
                    <span className="text-base text-[#202020] ml-3">{inquiry.admin_name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Card 2: 문의자 정보 */}
          <div className="flex flex-col gap-3">
            <span className="text-xs text-[#B0B0B0]">문의자 정보</span>
            <div className="flex justify-between p-5 rounded border border-black/[0.09]">
              <InfoItem
                icon={<img src={IcUserNew} alt="" />}
                label="이름"
                value={inquiry.user_info?.requester_name || "-"}
                cssValue=" whitespace-pre-line break-all w-[170px]"
              />
              <InfoItem
                icon={<img src={IcPhone} alt="" />}
                label="전화번호"
                value={formatPhoneNumber(inquiry.user_info?.phone || "") || "-"}
              />
              <InfoItem
                icon={<img src={IcMail01} alt="" className="w-7 h-7" />}
                label="이메일"
                value={inquiry.user_info?.email || "-"}
              />
              <InfoItem
                icon={<img src={IcUserCircle} alt="" className="w-7 h-7" />}
                label="직급"
                value={inquiry.user_info?.position || "-"}
              />
            </div>
          </div>
          {inquiry.selected_consulting && (
            <div>
              <span className="text-xs text-[#B0B0B0]">선택한 컨설팅</span>
              <div className="flex justify-between mt-2 px-4 py-3 rounded border-[1.5px] border-dashed border-black/[0.09] text-[#BBBBBB]  font-bold">
                {inquiry.selected_consulting}
              </div>
            </div>
          )}

          {/* Card 3: 첨부 에이전트 */}
          {(inquiry.agents?.length ?? 0) > 0 && (
            <div className="flex flex-col gap-3">
              <span className="text-xs text-[#B0B0B0]">첨부 에이전트</span>
              <div className="grid grid-cols-2 flex-wrap gap-3">
                {inquiry.agents?.map((agent) => (
                  <div
                    key={agent.id}
                    className="flex  items-center gap-3 p-4 bg-white border border-black/[0.09] rounded"
                  >
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#000000] truncate">
                        {agent.name}
                      </span>
                      <span className="text-xs text-[#B0B0B0] truncate">
                        {agent.provider || "-"}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/agents/${agent.id}`)}
                      className="text-xs font-bold text-[#FD5108]"
                    >
                      상세 보기
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Card 4: 문의 내용 */}
          <div className="flex flex-col gap-3">
            <span className="text-xs text-[#B0B0B0]">문의 내용</span>
            <div className="relative flex flex-col gap-6 p-5 border border-black/[0.09] rounded min-h-[300px] max-h-[850px]">
              {/* Messages area */}
              <div className="flex-1 flex flex-col gap-6 overflow-y-auto pr-2 custom-scrollbar ">
                {(inquiry.messages ?? []).map((reply) => (
                  <MessageBubble
                    key={reply.id}
                    reply={reply}
                    formatDateTime={(d) => formatDate(d, "YYYY-MM-DD HH:mm")}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat input */}
              {canReply && (
                <div className="flex flex-col items-center pt-10">
                  <div className="flex flex-col w-full bg-white border border-black/[0.09] rounded shadow-[0px_4px_20px_-8px_rgba(0,0,0,0.09),0px_0px_32px_0px_rgba(0,0,0,0.07)]">
                    <div className="px-5 pt-5 pb-2">
                      <textarea
                        ref={textareaRef}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="작성중인 요청사항..."
                        rows={1}
                        className="w-full resize-none bg-transparent text-base font-normal leading-6 tracking-[-0.01em] text-[#202020] placeholder:text-[#BBBBBB] outline-none"
                      />
                    </div>
                    <div className="flex justify-end p-2">
                      <button
                        type="button"
                        disabled={!replyText.trim() || isSending}
                        onClick={handleSendReply}
                        className="w-10 h-10 rounded-full bg-primary flex items-center justify-center disabled:opacity-40 transition-colors"
                      >
                        {/* <Send className="w-5 h-5 text-white" /> */}
                        <ChevronRight className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const InfoItem = ({
  icon,
  label,
  value,
  cssValue,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  cssValue?: string;
}) => (
  <div className="flex items-center gap-4">
    <div className="w-10 h-10 rounded-lg bg-black/[0.045] flex items-center justify-center ">
      {icon}
    </div>
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-[#B0B0B0]">{label}</span>
      <span className={`text-sm text-[#202020] ${cssValue}`}>{value}</span>
    </div>
  </div>
);

const MessageBubble = ({
  reply,
  formatDateTime,
}: {
  reply: InquiryReply;
  formatDateTime: (s: string) => string;
}) => {
  const isUser = reply.sender_role === "user";
  return (
    <div className={`flex flex-col gap-2 ${isUser ? "items-start" : "items-end"}`}>
      {isUser && (
        <div className="flex items-baseline gap-3">
          <span className="text-sm font-bold text-[#000000]">{reply.sender_name}</span>
          <span className="text-xs text-[#D8D8D8]">{formatDateTime(reply.created_at)}</span>
        </div>
      )}
      <div className="bg-[#00000010] rounded px-4 py-3 max-w-[80%]">
        <p className="text-base text-[#202020] whitespace-pre-wrap">{reply.content}</p>
      </div>
      {!isUser && (
        <div className="flex items-baseline gap-3">
          <span className="text-xs text-[#D8D8 D8]">{formatDateTime(reply.created_at)}</span>
          <span className="text-sm font-bold text-[#000000]">
            {reply.sender_name}
            <span> (관리자)</span>
          </span>
        </div>
      )}
    </div>
  );
};
